/* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
import type { SliderProps as PrimitiveSliderProps } from '@connected/ui/components/slider';

import { Slider as PrimitiveSlider } from '@connected/ui/components/slider';
import { useEffect, useState } from 'react';

import type { PrimativeFieldProps, PrimativeFormPropsOmit } from '../../lib/utils';

export type SliderProps = Omit<PrimitiveSliderProps, PrimativeFormPropsOmit | 'value' | 'onChange'> & PrimativeFieldProps<number[]> & {
    /**
     * The minimum value of the slider.
     * @default 0
     */
    min?: number;
    /**
     * The maximum value of the slider.
     * @default 100
     */
    max?: number;
    /**
     * The step size for each movement.
     * @default 1
     */
    stepSize?: number;
    /**
     * Optional background style for the track (e.g., gradient for color sliders)
     */
    trackStyle?: React.CSSProperties;
    /**
     * Optional custom class for the track
     */
    trackClassName?: string;
    /**
     * Whether to show the active range segment overlay.
     * Set to false to prevent blocking custom backgrounds.
     * @default true
     */
    showActiveSegment?: boolean;
};

export function Slider(props: SliderProps) {
    const {
        name: _name,
        value,
        onChange,
        onBlur: _onBlur,
        disabled,
        min = 0,
        max = 100,
        stepSize = 1,
        trackStyle,
        trackClassName,
        showActiveSegment = true,
        className,
        id: _id,
    } = props;

    const [sliderValue, setSliderValue] = useState<number[]>(value || [0]);

    useEffect(() => {
        setSliderValue(value || [0]);
    }, [value]);

    function handleValueChange(newValues: number[]) {
        setSliderValue(newValues);
        onChange(newValues);
    }

    return (
        <PrimitiveSlider
            value={sliderValue}
            onChange={handleValueChange}
            min={min}
            max={max}
            stepSize={stepSize}
            disabled={disabled}
            trackStyle={trackStyle}
            trackClassName={trackClassName}
            showActiveSegment={showActiveSegment}
            className={className}
        />
    );
}

Slider.displayName = 'Slider';
